import type { ThemeResponsive } from '@sites/site-theme/src/entity/schemas';

/** The three device widths the settings panels and the stylesheet share. */
export const THEME_BREAKPOINTS = ['desktop', 'tablet', 'mobile'] as const;

export type ThemeBreakpoint = (typeof THEME_BREAKPOINTS)[number];

/**
 * The value a property takes on desktop only.
 *
 * Tablet and mobile stay absent, which is not the same as equal: a reader falls back to `desktop`
 * for a breakpoint that has no value of its own, so leaving them out keeps the override
 * unset — the panel shows the tag as inheriting, and a later change to `desktop` follows through.
 */
export function onDesktop<T>(value: T | undefined): ThemeResponsive<T> | undefined {
    return value === undefined ? undefined : { desktop: value };
}

/**
 * What a property resolves to at one breakpoint, following the inheritance chain.
 *
 * Mobile falls back to tablet before desktop, matching how the widths nest — a tablet override is a
 * closer answer for a phone than the desktop value it was written to replace.
 */
export function resolveBreakpoint<T>(
    responsive: ThemeResponsive<T> | undefined,
    breakpoint: ThemeBreakpoint
): T | undefined {
    if (!responsive) {
        return undefined;
    }

    if (breakpoint === 'mobile') {
        return responsive.mobile ?? responsive.tablet ?? responsive.desktop;
    }

    if (breakpoint === 'tablet') {
        return responsive.tablet ?? responsive.desktop;
    }

    return responsive.desktop;
}
